import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ResponseModel, { PaginationRes } from "../../../models/response.model";
import {
  assignNewStockToFloorApi,
  cancelReplaceReturnRequestApi,
  getReplaceReturnRequestsApi,
  replaceReturnRequestApi,
  resolveReplaceReturnRequestApi,
  reviewReplaceReturnRequestApi,
  vendorDispatchForReplacementApi,
  receiveReplacementFromVendorApi,
  shrinkApi,
} from "../../../api/Factory/materialReturn.api";
import { IFloorMaterialReturnRequest } from "../../../models/factory/floor/floorReturnRequest.model";

interface MaterialReturnState {
  returnRequests: IFloorMaterialReturnRequest[];
  pagination?: PaginationRes;
  loading: {
    getReplaceReturnRequestsLoading: boolean;
    replaceReturnRequestLoading: boolean;
    cancelReplaceReturnRequestLoading: boolean;
    reviewReplaceReturnRequestLoading: boolean;
    resolveReplaceReturnRequestLoading: boolean;
    assignNewStockToFloorLoading: boolean;
    vendorDispatchForReplacementLoading: boolean;
  };
}

const initialState: MaterialReturnState = {
  returnRequests: [],
  pagination: undefined,
  loading: {
    getReplaceReturnRequestsLoading: false,
    replaceReturnRequestLoading: false,
    cancelReplaceReturnRequestLoading: false,
    reviewReplaceReturnRequestLoading: false,
    resolveReplaceReturnRequestLoading: false,
    assignNewStockToFloorLoading: false,
    vendorDispatchForReplacementLoading: false,
  },
};

export const replaceReturnRequest = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    materialId: string;
    quantity: number;
    unit: string;
    reason: string;
  },
  { rejectValue: string }
>(
  "newFloor/replaceReturnRequest",
  async (
    { requestId, materialId, quantity, unit, reason },
    { rejectWithValue },
  ) => {
    try {
      return await replaceReturnRequestApi({
        requestId,
        materialId,
        quantity,
        unit,
        reason,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to create replace return request",
      );
    }
  },
);

export const getReplaceReturnRequests = createAsyncThunk<
  ResponseModel,
  { page: number; limit: number; status?: string },
  { rejectValue: string }
>(
  "newFloor/getReplaceReturnRequests",
  async ({ page, limit, status }, { rejectWithValue }) => {
    try {
      return await getReplaceReturnRequestsApi(page, limit, status);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to get replace return requests",
      );
    }
  },
);

export const cancelReplaceReturnRequest = createAsyncThunk<
  ResponseModel,
  { requestId: string },
  { rejectValue: string }
>(
  "newFloor/cancelReplaceReturnRequest",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      return await cancelReplaceReturnRequestApi(requestId);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to cancel replace return request",
      );
    }
  },
);

export const reviewReplaceReturnRequest = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    status: "accepted" | "rejected";
    remark?: string;
  },
  { rejectValue: string }
>(
  "newFloor/reviewReplaceReturnRequest",
  async ({ requestId, status, remark }, { rejectWithValue }) => {
    try {
      return await reviewReplaceReturnRequestApi(requestId, status, remark);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to review replace return request",
      );
    }
  },
);
export const resolveReplaceReturnRequest = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    resolution: "replace" | "repair" | "return";
    remark?: string;
  },
  { rejectValue: string }
>(
  "newFloor/resolveReplaceReturnRequest",
  async ({ requestId, resolution, remark }, { rejectWithValue }) => {
    try {
      return await resolveReplaceReturnRequestApi(
        requestId,
        resolution,
        remark,
      );
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to resolve replace return request",
      );
    }
  },
);

export const assignNewStockToFloor = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    materialId: string;
    quantity: number;
    unit: string;
  },
  { rejectValue: string }
>(
  "newFloor/assignNewStockToFloor",
  async ({ requestId, materialId, quantity, unit }, { rejectWithValue }) => {
    try {
      return await assignNewStockToFloorApi(
        requestId,
        materialId,
        quantity,
        unit,
      );
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to assign new stock to floor",
      );
    }
  },
);

export const vendorDispatchForReplacement = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    vendorId: string;
    quantity: number;
    trasportCost: number;
    otherCosts: number;
    remark?: string;
  },
  { rejectValue: string }
>(
  "newFloor/vendorDispatchForReplacement",
  async (
    { requestId, vendorId, quantity, trasportCost, otherCosts, remark },
    { rejectWithValue },
  ) => {
    try {
      return await vendorDispatchForReplacementApi(
        requestId,
        vendorId,
        quantity,
        trasportCost,
        otherCosts,
        remark,
      );
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to dispatch replacement to vendor",
      );
    }
  },
);

export const receiveReplacementFromVendor = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    quantity: number;
    unit: string;
    vendorId: string;
    billNumber: string;
    transportCost: number;
    otherCosts: number;
    repairCost: number;
    remark?: string;
  },
  { rejectValue: string }
>(
  "newFloor/receiveReplacementFromVendor",
  async (
    {
      requestId,
      vendorId,
      quantity,
      unit,
      billNumber,
      transportCost,
      otherCosts,
      repairCost,
      remark,
    },
    { rejectWithValue },
  ) => {
    try {
      return await receiveReplacementFromVendorApi(requestId, {
        quantity,
        unit,
        vendorId,
        billNumber,
        transportCost,
        otherCosts,
        repairCost,
        remark,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to receive replacement from vendor",
      );
    }
  },
);

export const shrink = createAsyncThunk<
  ResponseModel,
  {
    requestId: string;
    quantity: number;
    unit: string;
  },
  { rejectValue: string }
>(
  "newFloor/shrink",
  async ({ requestId, quantity, unit }, { rejectWithValue }) => {
    try {
      return await shrinkApi(requestId, quantity, unit);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to shrink material");
    }
  },
);

const materialReturnSlice = createSlice({
  name: "materialReturn",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReplaceReturnRequests.pending, (state) => {
        state.loading.getReplaceReturnRequestsLoading = true;
      })
      .addCase(getReplaceReturnRequests.fulfilled, (state, action) => {
        state.loading.getReplaceReturnRequestsLoading = false;
        state.returnRequests = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getReplaceReturnRequests.rejected, (state) => {
        state.loading.getReplaceReturnRequestsLoading = false;
      })
      .addCase(replaceReturnRequest.pending, (state) => {
        state.loading.replaceReturnRequestLoading = true;
      })
      .addCase(replaceReturnRequest.fulfilled, (state) => {
        state.loading.replaceReturnRequestLoading = false;
      })
      .addCase(replaceReturnRequest.rejected, (state) => {
        state.loading.replaceReturnRequestLoading = false;
      })
      .addCase(cancelReplaceReturnRequest.pending, (state) => {
        state.loading.cancelReplaceReturnRequestLoading = true;
      })
      .addCase(cancelReplaceReturnRequest.fulfilled, (state) => {
        state.loading.cancelReplaceReturnRequestLoading = false;
      })
      .addCase(cancelReplaceReturnRequest.rejected, (state) => {
        state.loading.cancelReplaceReturnRequestLoading = false;
      })
      .addCase(reviewReplaceReturnRequest.pending, (state) => {
        state.loading.reviewReplaceReturnRequestLoading = true;
      })
      .addCase(reviewReplaceReturnRequest.fulfilled, (state) => {
        state.loading.reviewReplaceReturnRequestLoading = false;
      })
      .addCase(reviewReplaceReturnRequest.rejected, (state) => {
        state.loading.reviewReplaceReturnRequestLoading = false;
      })
      .addCase(resolveReplaceReturnRequest.pending, (state) => {
        state.loading.resolveReplaceReturnRequestLoading = true;
      })
      .addCase(resolveReplaceReturnRequest.fulfilled, (state) => {
        state.loading.resolveReplaceReturnRequestLoading = false;
      })
      .addCase(resolveReplaceReturnRequest.rejected, (state) => {
        state.loading.resolveReplaceReturnRequestLoading = false;
      })
      .addCase(assignNewStockToFloor.pending, (state) => {
        state.loading.assignNewStockToFloorLoading = true;
      })
      .addCase(assignNewStockToFloor.fulfilled, (state) => {
        state.loading.assignNewStockToFloorLoading = false;
      })
      .addCase(assignNewStockToFloor.rejected, (state) => {
        state.loading.assignNewStockToFloorLoading = false;
      })
      .addCase(vendorDispatchForReplacement.pending, (state) => {
        state.loading.vendorDispatchForReplacementLoading = true;
      })
      .addCase(vendorDispatchForReplacement.fulfilled, (state) => {
        state.loading.vendorDispatchForReplacementLoading = false;
      })
      .addCase(vendorDispatchForReplacement.rejected, (state) => {
        state.loading.vendorDispatchForReplacementLoading = false;
      });
  },
});

export default materialReturnSlice.reducer;
